import React, { useState } from 'react';
import { Card, Image, Icon, Label, Button, Popup, TextArea, Divider, Loader } from 'semantic-ui-react';
import { NavLink } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { formatDistanceToNow } from 'date-fns';
import {
  enUS, zhCN, es, hi, ar, pt, ru, bn, fr, de, ja, ro, it, hu, el, tr, sr
} from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { FeedComment, FeedCommentTargetType, FeedItem, FeedItemType, PostVisibility } from '../../app/models/Playground';
import { useStore } from '../../app/stores/store';
import { TournamentFeedCard } from '../tournaments/TournamentFeedCard';

interface Props {
  item: FeedItem;
  currentUserId?: string;
}

const TYPE_BORDER: Record<number, string> = {
  [FeedItemType.NeedOnePlayer]: '#db2828',
  [FeedItemType.PublicGame]: '#fbbd08',
  [FeedItemType.FriendActivity]: '#f2711c',
  [FeedItemType.UserPost]: '#2185d0',
  [FeedItemType.SharedPost]: '#a333c8',
  [FeedItemType.NewsItem]: '#00b5ad',
};

const TYPE_ICON: Record<number, any> = {
  [FeedItemType.NeedOnePlayer]: 'fire',
  [FeedItemType.PublicGame]: 'soccer',
  [FeedItemType.FriendActivity]: 'users',
  [FeedItemType.UserPost]: 'pencil',
  [FeedItemType.SharedPost]: 'share',
  [FeedItemType.NewsItem]: 'newspaper',
};

const TYPE_COLOR: Record<number, any> = {
  [FeedItemType.NeedOnePlayer]: 'red',
  [FeedItemType.PublicGame]: 'yellow',
  [FeedItemType.FriendActivity]: 'orange',
  [FeedItemType.UserPost]: 'blue',
  [FeedItemType.SharedPost]: 'purple',
  [FeedItemType.NewsItem]: 'teal',
};

const TYPE_LABEL_KEY: Record<number, string> = {
  [FeedItemType.NeedOnePlayer]: 'playground.card.needOnePlayer',
  [FeedItemType.PublicGame]: 'playground.card.publicGame',
  [FeedItemType.FriendActivity]: 'playground.card.friendActivity',
  [FeedItemType.UserPost]: 'playground.card.userPost',
  [FeedItemType.SharedPost]: 'playground.card.sharedPost',
  [FeedItemType.NewsItem]: 'playground.card.newsItem',
};

const VISIBILITY_ICON: Record<number, { icon: any; labelKey: string }> = {
  [PostVisibility.FriendsOnly]: { icon: 'lock', labelKey: 'playground.visibility.friendsOnly' },
  [PostVisibility.Followers]: { icon: 'users', labelKey: 'playground.visibility.followers' },
  [PostVisibility.Standard]: { icon: 'globe', labelKey: 'playground.visibility.public' },
};

const getDateLocale = (language: string) => {
  switch (language) {
    case 'en': return enUS;
    case 'zh': return zhCN;
    case 'es': return es;
    case 'hi': return hi;
    case 'ar': return ar;
    case 'pt': return pt;
    case 'ru': return ru;
    case 'bn': return bn;
    case 'fr': return fr;
    case 'de': return de;
    case 'ja': return ja;
    case 'ro': return ro;
    case 'it': return it;
    case 'hu': return hu;
    case 'el': return el;
    case 'tr': return tr;
    case 'sr':
    case 'srLatn': return sr;
    default: return enUS;
  }
};

const convertToLatinScript = (text: string) => {
  const cyrillicToLatin: Record<string, string> = {
    'око': 'oko', 'пре': 'pre',
    'сати': 'sati', 'сат': 'sat',
    'минута': 'minuta', 'минут': 'minut',
    'секунди': 'sekundi', 'секунд': 'sekund',
    'дан': 'dan', 'дана': 'dana',
    'месец': 'mesec', 'месеца': 'meseca',
    'година': 'godina', 'године': 'godine',
  };
  let result = text;
  Object.entries(cyrillicToLatin).forEach(([cyr, lat]) => {
    result = result.replace(new RegExp(cyr, 'g'), lat);
  });
  return result;
};

const useTimeAgo = (date: Date) => {
  const { t, i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);
  const raw = formatDistanceToNow(date, { locale });
  const time = i18n.language === 'srLatn' ? convertToLatinScript(raw) : raw;
  return t('gameDetailedChat.timeAgo', { time });
};

// ── Comment Thread ────────────────────────────────────────────────────────────
function CommentItem({ c, postId, depth, currentUserId }: {
  c: FeedComment;
  postId: string;
  depth: number;
  currentUserId?: string;
}) {
  const { playgroundStore } = useStore();
  const { t } = useTranslation();
  const [replyingTo, setReplyingTo] = useState(false);
  const [replyText, setReplyText] = useState('');
  const timeAgo = useTimeAgo(c.createdAt);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    await playgroundStore.addComment({
      content: replyText,
      parentCommentId: c.id,
      targetType: FeedCommentTargetType.Post,
      feedPostId: postId,
    });
    setReplyText('');
    setReplyingTo(false);
  };

  return (
    <div style={{
      padding: '8px 0',
      borderBottom: depth === 0 ? '1px solid #f0f0f0' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <Image
          src={c.author.imageUrl || '/assets/user.png'}
          circular
          style={{ width: 28, height: 28, minWidth: 28 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{
            background: '#f4f6fb',
            borderRadius: 12,
            padding: '6px 12px',
            display: 'inline-block',
            maxWidth: '100%',
          }}>
            <span style={{ fontWeight: 700, fontSize: '0.82em', marginRight: 6, color: '#2185d0' }}>
              {c.author.fullName}
            </span>
            <span style={{ fontSize: '0.88em', color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {c.content}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 4, paddingLeft: 4, alignItems: 'center' }}>
            <button
              onClick={() => playgroundStore.toggleCommentLike(c.id, postId)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: c.isLikedByCurrentUser ? '#2185d0' : '#aaa',
                fontSize: '0.78em', padding: 0, fontWeight: c.isLikedByCurrentUser ? 700 : 400,
              }}
            >
              <Icon name='thumbs up outline' style={{ margin: 0 }} />
              {c.likeCount > 0 ? ` ${c.likeCount}` : ''} {t('playground.like')}
            </button>

            {depth < 2 && (
              <button
                onClick={() => setReplyingTo(!replyingTo)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '0.78em', padding: 0 }}
              >
                <Icon name='reply' style={{ margin: 0 }} />
                {t('playground.reply')}
              </button>
            )}

            {c.author.userId === currentUserId && (
              <button
                onClick={() => playgroundStore.deleteComment(c.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#db2828', fontSize: '0.78em', padding: 0 }}
              >
                <Icon name='trash alternate outline' style={{ margin: 0 }} />
                {t('playground.delete')}
              </button>
            )}

            <span style={{ color: '#ccc', fontSize: '0.74em' }}>
              <Icon name='clock outline' style={{ margin: 0 }} />
              {timeAgo}
            </span>
          </div>

          {replyingTo && (
            <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <TextArea
                  value={replyText}
                  onChange={(_, d) => {
                    const val = d.value as string;
                    if (val.length <= 500) setReplyText(val);
                  }}
                  rows={2}
                  style={{
                    width: '100%',
                    minHeight: 48,
                    resize: 'vertical',
                    fontSize: '0.85em',
                    borderRadius: 8,
                    paddingBottom: 18,
                  }}
                  placeholder={t('playground.writeReply')}
                />
                <span style={{
                  position: 'absolute', bottom: 6, right: 8,
                  fontSize: '0.72em',
                  color: replyText.length > 450 ? '#db2828' : '#aaa',
                }}>
                  {replyText.length}/500
                </span>
              </div>
              <Button size='mini' primary onClick={handleReply} disabled={!replyText.trim()}>
                <Icon name='send' style={{ margin: 0 }} />
              </Button>
            </div>
          )}

          {c.replies && c.replies.length > 0 && (
            <CommentThread comments={c.replies} postId={postId} depth={depth + 1} currentUserId={currentUserId} />
          )}
        </div>
      </div>
    </div>
  );
}

function CommentThread({ comments, postId, depth = 0, currentUserId }: {
  comments: FeedComment[];
  postId: string;
  depth?: number;
  currentUserId?: string;
}) {
  return (
    <div style={{ marginLeft: depth > 0 ? 24 : 0 }}>
      {comments.map(c => (
        <CommentItem key={c.id} c={c} postId={postId} depth={depth} currentUserId={currentUserId} />
      ))}
    </div>
  );
}

// ── Main FeedCard ─────────────────────────────────────────────────────────────

const FeedCard = observer(({ item, currentUserId }: Props) => {
  const { playgroundStore } = useStore();
  const { t } = useTranslation();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  const isMobile = window.innerWidth < 600;

  // ── Tournament items are fully delegated to TournamentFeedCard ────────────
  if (item.type === FeedItemType.Tournament) {
    if (!item.tournament) return null;
    return <TournamentFeedCard tournament={item.tournament} />;
  }

  // ── NeedOnePlayer / PublicGame / FriendActivity ───────────────────────────
  if (
    item.type === FeedItemType.NeedOnePlayer ||
    item.type === FeedItemType.PublicGame ||
    item.type === FeedItemType.FriendActivity
  ) {
    if (!item.game) return null;

    const game = item.game;
    const isNeedOne = item.type === FeedItemType.NeedOnePlayer;
    const accentColor = isNeedOne ? '#db2828' : '#2185d0';
    const labelColor: 'red' | 'blue' | 'teal' = isNeedOne ? 'red' : 'blue';

    return (
      <Card
        fluid
        style={{
          borderRadius: 12,
          borderTop: `3px solid ${accentColor}`,
          boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
          marginBottom: 12,
          overflow: 'hidden',
        }}
      >
        <Card.Content>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Label color={labelColor} size='mini' style={{ borderRadius: 20 }}>
              <Icon name={isNeedOne ? 'fire' : 'game'} />
              {isNeedOne
                ? t('playground.urgency.oneSpotLeft')
                : item.type === FeedItemType.FriendActivity
                  ? t('playground.filter.friendActivity')
                  : t('playground.filter.publicGames')}
            </Label>
            <span style={{ fontSize: '0.76em', color: '#aaa' }}>
              <Icon name='clock outline' color='grey' size='small' />
              {formatDistanceToNow(new Date(game.dateTimeStart), { addSuffix: true })}
            </span>
          </div>

          <div style={{ fontWeight: 700, fontSize: '0.97em', marginBottom: 4, color: '#1a1a2e' }}>
            {game.gameName}
          </div>

          <div style={{ fontSize: '0.82em', color: '#555', marginBottom: 8, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span><Icon name='soccer' color='grey' size='small' />{game.sportName}</span>
            {game.distanceKm != null && (
              <span><Icon name='map marker alternate' color='teal' size='small' />{game.distanceKm} km</span>
            )}
            <span>
              <Icon name='users' color='grey' size='small' />
              {game.currentParticipants}/{game.maxParticipants ?? '?'}
            </span>
          </div>
        </Card.Content>

        <Card.Content extra style={{ background: '#fafafa', paddingTop: 6, paddingBottom: 6 }}>
          <Button
            as={NavLink}
            to={`/games/${game.id}`}
            color={labelColor}
            size='mini'
            style={{ borderRadius: 20 }}
          >
            <Icon name='sign in' />
            {t('playground.urgency.join')}
          </Button>
        </Card.Content>
      </Card>
    );
  }

  // ── UserPost / SharedPost ─────────────────────────────────────────────────
  const isOwnPost = item.author?.userId === currentUserId;

  const handleToggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && item.topComments.length === 0 && item.commentCount > 0) {
      setLoadingComments(true);
      // Comments are already in topComments from the feed — no extra fetch needed
      setLoadingComments(false);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    await playgroundStore.addComment({
      content: commentText.trim(),
      targetType: FeedCommentTargetType.Post,
      feedPostId: item.id,
    });
    setCommentText('');
  };

  const handleLike = () => playgroundStore.togglePostLike(item.id);
  const handleNotRelevant = () => playgroundStore.markNotRelevant(item.id);
  const handleShare = () => playgroundStore.sharePost(item.id);
  const handleDelete = () => playgroundStore.deletePost(item.id);

  return (
    <Card
      fluid
      style={{
        borderRadius: 12,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <Card.Content>

        {/* ── Author row ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <img
            src={item.author?.imageUrl || '/assets/user.png'}
            style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '1px solid #eee' }}
            alt={item.author?.fullName}
          />
          <div style={{ flex: 1 }}>
            <NavLink
              to={`/profiles/${item.author?.userId}`}
              style={{ fontWeight: 700, fontSize: '0.92em', color: '#1a1a2e', textDecoration: 'none' }}
            >
              {item.author?.fullName}
            </NavLink>
            {item.author?.influenceBadge && (
              <span style={{ marginLeft: 6, fontSize: '0.85em' }}>{item.author.influenceBadge}</span>
            )}
            <div style={{ fontSize: '0.74em', color: '#aaa' }}>
              <Icon name='clock outline' size='small' />
              {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
            </div>
          </div>

          {isOwnPost && (
            <Button
              basic
              icon='trash alternate outline'
              size='mini'
              color='red'
              onClick={handleDelete}
              title={t('playground.delete')}
            />
          )}
        </div>

        {/* ── Shared-by banner ── */}
        {item.type === FeedItemType.SharedPost && item.sharedByUserName && (
          <div style={{
            background: '#f0f4ff', borderRadius: 8, padding: '4px 10px',
            marginBottom: 8, fontSize: '0.8em', color: '#555',
          }}>
            <Icon name='retweet' color='blue' />
            {t('playground.sharedBy', { name: item.sharedByUserName })}
          </div>
        )}

        {/* ── Content ── */}
        {item.content && (
          <Card.Description style={{ fontSize: '0.92em', lineHeight: 1.6, marginBottom: item.mediaUrl ? 10 : 0 }}>
            {item.content}
          </Card.Description>
        )}

        {/* ── Media / OG preview ── */}
        {item.mediaUrl && !item.ogPreview && (
          <a href={item.mediaUrl} target='_blank' rel='noreferrer'>
            <div style={{
              background: '#f8f8f8', borderRadius: 8, padding: '6px 10px',
              marginTop: 8, fontSize: '0.8em', color: '#2185d0', wordBreak: 'break-all',
            }}>
              <Icon name='linkify' /> {item.mediaUrl}
            </div>
          </a>
        )}

        {item.ogPreview && (
          <a
            href={item.ogPreview.url || item.mediaUrl}
            target='_blank'
            rel='noreferrer'
            style={{ textDecoration: 'none' }}
          >
            <div style={{
              border: '1px solid #e8eaf6', borderRadius: 10,
              overflow: 'hidden', marginTop: 10,
            }}>
              {item.ogPreview.imageUrl && (
                <img
                  src={item.ogPreview.imageUrl}
                  alt={item.ogPreview.title}
                  style={{
                    width: '100%',
                    maxHeight: isMobile ? 220 : 360,
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                    background: '#f4f4f4',
                  }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div style={{ padding: '8px 12px' }}>
                {item.ogPreview.siteName && (
                  <div style={{ fontSize: '0.72em', color: '#aaa', marginBottom: 2 }}>
                    {item.ogPreview.siteName}
                  </div>
                )}
                {item.ogPreview.title && (
                  <div style={{ fontWeight: 600, fontSize: '0.88em', color: '#1a1a2e', marginBottom: 2 }}>
                    {item.ogPreview.title}
                  </div>
                )}
                {item.ogPreview.description && (
                  <div style={{ fontSize: '0.78em', color: '#666', lineHeight: 1.4 }}>
                    {item.ogPreview.description.slice(0, isMobile ? 100 : 160)}
                    {item.ogPreview.description.length > (isMobile ? 100 : 160) ? '…' : ''}
                  </div>
                )}
              </div>
            </div>
          </a>
        )}

      </Card.Content>

      {/* ── Engagement bar ── */}
      <Card.Content extra style={{ background: '#fafafa', paddingTop: 6, paddingBottom: 6 }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Like */}
          <Button
            basic
            size='mini'
            color={item.isLikedByCurrentUser ? 'blue' : undefined}
            onClick={handleLike}
            style={{ borderRadius: 20 }}
          >
            <Icon name={item.isLikedByCurrentUser ? 'thumbs up' : 'thumbs up outline'}
              color={item.isLikedByCurrentUser ? 'blue' : 'grey'} />
            {item.likeCount > 0 && item.likeCount}
          </Button>

          {/* Comment toggle */}
          <Button
            basic
            size='mini'
            color={showComments ? 'blue' : undefined}
            onClick={handleToggleComments}
            style={{ borderRadius: 20 }}
          >
            <Icon name={showComments ? 'comment' : 'comment outline'}
              color={showComments ? 'blue' : 'grey'} />
            {item.commentCount > 0 && item.commentCount}
          </Button>

          {/* Share */}
          <Button
            basic
            size='mini'
            onClick={handleShare}
            style={{ borderRadius: 20 }}
          >
            <Icon name='share alternate' color='grey' />
            {item.shareCount > 0 && item.shareCount}
          </Button>

          {/* Not relevant (only for others' posts) */}
          {!isOwnPost && (
            <Button
              basic
              size='mini'
              color={item.isNotRelevantByCurrentUser ? 'red' : undefined}
              onClick={handleNotRelevant}
              style={{ borderRadius: 20, marginLeft: 'auto' }}
              title={t('playground.notRelevant')}
            >
              <Icon name='eye slash outline'
                color={item.isNotRelevantByCurrentUser ? 'red' : 'grey'} />
            </Button>
          )}
        </div>

        {/* ── Comments section ── */}
        {showComments && (
          <div style={{ marginTop: 10 }}>
            <Divider />

            {loadingComments ? (
              <Loader active inline='centered' size='small' style={{ margin: '10px auto' }} />
            ) : item.topComments.length > 0 ? (
              <CommentThread
                comments={item.topComments}
                postId={item.id}
                depth={0}
                currentUserId={currentUserId}
              />
            ) : (
              <p style={{ fontSize: '0.8em', color: '#aaa', marginBottom: 8, textAlign: 'center' }}>
                <Icon name='comment outline' color='grey' />
                {t('playground.news.noComments')}
              </p>
            )}

            {/* New comment input */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10 }}>
              <TextArea
                value={commentText}
                onChange={(_, d) => setCommentText(d.value as string)}
                rows={1}
                style={{ flex: 1, minHeight: 36, resize: 'none', fontSize: '0.88em', borderRadius: 8 }}
                placeholder={t('playground.writeComment')}
              />
              <Button
                primary
                size='small'
                disabled={!commentText.trim()}
                onClick={handleComment}
                style={{ borderRadius: 8 }}
              >
                <Icon name='send' />
                {t('playground.send')}
              </Button>
            </div>
          </div>
        )}
      </Card.Content>
    </Card>
  );
});

export default FeedCard;